import React, { useState } from 'react';
import SEO from '../SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Activity, BarChart2, CheckCircle, Check, X, ChevronDown, ChevronUp, Globe, MousePointer, Shield, TrendingUp, Zap, Target, Layout, Clock, Play, Server, Lock, Code , Star} from 'lucide-react';

interface LandingPageProps {
    onLogin?: () => void;
    onNavigate?: (page: string) => void;
}

const IdOrganicWebsiteTraffic: React.FC<LandingPageProps> = ({ onLogin, onNavigate }) => {
    const [scrolled, setScrolled] = useState(false);

    // Schema.org Structured Data
    const schema = [
        {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Generator Lalu Lintas Web Organik",
            "description": "Layanan lalu lintas organik premium untuk analisis GA4, kontrol durasi sesi, dan pemodelan perilaku dengan IP residensial.",
            "brand": { "@type": "Brand", "name": "Traffic Creator" }
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Beranda", "item": "https://traffic-creator.com" },
                { "@type": "ListItem", "position": 2, "name": "Lalu Lintas Web Organik", "item": "https://traffic-creator.com/id/beli-traffic-organik" }
            ]
        }
    ];

    const benefits = [
        {
            icon: <Search className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Analisis CTR Organik",
            desc: "Gunakan alur pencarian dan klik yang terkontrol untuk membaca performa halaman di laporan analitik."
        },
        {
            icon: <Clock className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Tingkatkan Waktu Kunjungan",
            desc: "Pengunjung tetap berada di situs Anda selama beberapa menit, menggulir konten, dan berinteraksi, secara drastis mengurangi rasio pentalan."
        },
        {
            icon: <Globe className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Analisis SEO Lokal",
            desc: "Targetkan lalu lintas dari kota atau wilayah tertentu untuk membandingkan kualitas sesi per pasar."
        },
        {
            icon: <Shield className="w-6 h-6 text-[#ff5a0a]" />,
            title: "Perilaku Sesi Terkontrol",
            desc: "Gerakan mouse, pengguliran, dan klik dapat dikonfigurasi untuk simulasi perilaku yang realistis."
        }
    ];

    const comparisons = [
        { feature: "Sumber Lalu Lintas", us: "IP Residensial Asli", them: "Proksi Pusat Data" },
        { feature: "Perilaku", us: "Gulir, Klik, Membaca", them: "Kunjungan Statis" },
        { feature: "Google Analytics", us: "Terlihat di GA4", them: "Sering Difilter / Bot" },
        { feature: "Efek pada Data", us: "Sesi Lebih Terukur", them: "Netral atau Mengganggu" },
        { feature: "Kontrol Kualitas", us: "Parameter Jelas", them: "Risiko Data Tinggi" },
    ];

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 font-sans overflow-x-hidden">
            <SEO
                title="Beli Lalu Lintas Web Organik untuk Analisis GA4"
                description="Dapatkan traffic organik bergaya residensial dengan kontrol durasi, scroll, geo, dan laporan GA4 untuk analisis halaman."
                keywords="lalu lintas web organik 2026, beli lalu lintas organik, seo traffic, analisis ctr, laporan GA4, pengunjung residensial, bot lalu lintas ai"
                schema={schema}
                type="product"
            />

            {/* Hreflang Tags */}

            {/* Navigation */}
            <nav className="fixed w-full top-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md border-b border-gray-100/50">
                <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-2 group cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
                        <Link to="/" className="flex items-center gap-2">
                            <span className="text-2xl font-black text-[#ff5a0a] tracking-tight group-hover:scale-105 transition-transform">TRAFFIC</span>
                            <span className="hidden sm:inline-block text-xs font-bold bg-black text-white px-2 py-1 rounded-sm uppercase tracking-wide">Creator</span>
                        </Link>
                    </div>
                    <div className="flex items-center gap-8">
                        <div className="hidden md:flex gap-8 text-sm font-bold uppercase tracking-wide text-gray-500">
                            <a href="#how-it-works" className="hover:text-[#ff5a0a] transition-colors relative group">
                                Cara Kerja
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </a>
                            <a href="#benefits" className="hover:text-[#ff5a0a] transition-colors relative group">
                                Fitur
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </a>
                            <Link to="/#pricing" className="hover:text-[#ff5a0a] transition-colors relative group">
                                Harga
                                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#ff5a0a] transition-all group-hover:w-full"></span>
                            </Link>
                        </div>
                        <Link to="/login" className="bg-black text-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#ff5a0a] transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center rounded-sm">
                            Login Dashboard
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-32 px-6 relative overflow-hidden bg-white">
                <div className="absolute top-0 right-0 w-[50%] h-[50%] bg-gradient-to-b from-orange-50/50 to-transparent pointer-events-none rounded-bl-[100px]"></div>

                <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
                    <div className="animate-in fade-in slide-in-from-bottom-8 duration-700">
                        <div className="inline-flex items-center gap-2 bg-orange-50 text-[#ff5a0a] border border-orange-100 px-4 py-2 text-xs font-bold uppercase tracking-widest mb-8 rounded-full shadow-sm">
                            <Zap size={14} className="fill-[#ff5a0a]" /> Mesin Intelijen SEO
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-[0.9] mb-8 text-gray-900">
                            ANALISIS KUALITAS TRAFFIC <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff5a0a] to-orange-500">ORGANIK</span>.
                        </h1>
                        <p className="text-xl text-gray-500 mb-10 max-w-lg leading-relaxed font-medium">
                            Generator lalu lintas web organik untuk profesional SEO yang membutuhkan kontrol CTR, waktu kunjungan, dan laporan GA4 yang mudah dianalisis.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link to="/signup" className="group bg-[#ff5a0a] text-white px-8 py-4 text-sm font-bold uppercase tracking-wider hover:bg-black transition-all shadow-xl hover:shadow-2xl flex items-center justify-center gap-3 rounded-sm transform hover:-translate-y-1">
                                Luncurkan Kampanye SEO
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <a href="#demo" className="bg-white border border-gray-200 text-gray-900 px-8 py-4 text-sm font-bold uppercase tracking-wider hover:border-[#ff5a0a] hover:text-[#ff5a0a] transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 rounded-sm group">
                                <Play size={16} className="group-hover:fill-[#ff5a0a]" /> Lihat Demo Langsung
                            </a>
                        </div>
                    </div>

                    {/* Visual: Abstract SEO Graph */}
                    <div className="relative h-[550px] perspective-1000 group">
                        <div className="absolute inset-0 bg-white border border-gray-200 shadow-2xl rounded-2xl p-6 overflow-hidden transform transition-transform duration-700 group-hover:-translate-y-2">
                            <div className="border-b border-gray-100 pb-4 mb-6 flex gap-2">
                                <div className="w-3 h-3 rounded-full bg-red-400/80"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-400/80"></div>
                                <div className="w-3 h-3 rounded-full bg-green-400/80"></div>
                            </div>

                            <div className="space-y-6">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className={`p-5 rounded-lg border transition-all duration-500 ${i === 1 ? 'border-[#ff5a0a]/20 bg-orange-50/30' : 'border-gray-50 bg-white'}`}>
                                        {i === 1 && <div className="absolute top-0 left-0 w-1 h-full bg-[#ff5a0a]"></div>}
                                        <div className="w-3/4 h-5 rounded-sm mb-3 bg-gray-100"></div>
                                        <div className="space-y-2">
                                            <div className="w-full h-2 bg-gray-100 rounded-sm"></div>
                                            <div className="w-5/6 h-2 bg-gray-100 rounded-sm"></div>
                                        </div>
                                        {i === 1 && (
                                            <div className="mt-4 flex items-center gap-2 text-[#ff5a0a] text-xs font-bold uppercase tracking-wider animate-pulse">
                                                <TrendingUp size={12} /> Traffic quality • CTR analysis active
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div className="absolute bottom-6 right-6 bg-gray-900/95 backdrop-blur-md text-white p-5 rounded-xl shadow-2xl z-20 border border-gray-800">
                                <div className="text-[10px] font-bold uppercase tracking-widest text-[#ff5a0a] mb-2">Performa</div>
                                <div className="text-4xl font-black mb-1">18.4%</div>
                                <div className="text-xs text-gray-400 font-medium">CTR Organik (7 Hari)</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Benefits Grid */}
            <section id="benefits" className="py-32 px-6 bg-white relative">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-24">
                        <h2 className="text-4xl font-black uppercase tracking-tight mb-6">Mengapa Kualitas Sesi Penting?</h2>
                        <div className="w-16 h-1.5 bg-[#ff5a0a] mx-auto mb-8 rounded-full"></div>
                        <p className="text-gray-500 max-w-2xl mx-auto text-lg leading-relaxed">
                            Kampanye SEO modern membutuhkan data perilaku yang bisa dibaca, bukan sekadar hit kosong. Traffic Creator membantu Anda menguji <span className="text-gray-900 font-bold">kualitas sesi</span> dengan durasi, scroll, dan navigasi yang dapat dikontrol.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
                        {benefits.map((b, i) => (
                            <div key={i} className="flex gap-8 p-10 border border-gray-100 hover:border-[#ff5a0a]/30 bg-white hover:bg-orange-50/20 shadow-sm transition-all duration-300 group rounded-2xl relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-1 h-0 bg-[#ff5a0a] group-hover:h-full transition-all duration-300"></div>
                                <div className="shrink-0">
                                    <div className="bg-orange-50 w-16 h-16 flex items-center justify-center rounded-2xl group-hover:bg-[#ff5a0a] transition-all text-[#ff5a0a] group-hover:text-white">
                                        {b.icon}
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#ff5a0a] transition-colors">{b.title}</h3>
                                    <p className="text-gray-500 leading-relaxed font-medium">{b.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-24 px-6 bg-gray-50 border-y border-gray-100">
                <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10">
                    <div>
                        <h2 className="text-3xl font-black uppercase tracking-tight mb-5">Catatan Kualitas Teknis</h2>
                        <p className="text-gray-600 leading-relaxed font-medium mb-4">
                            Halaman ini berfokus pada analisis traffic organik bergaya residensial, bukan janji posisi pencarian. Setiap kampanye dapat mengatur negara, perangkat, durasi sesi, kedalaman scroll, dan jumlah halaman yang dikunjungi agar data GA4 lebih mudah dibaca.
                        </p>
                        <p className="text-gray-600 leading-relaxed font-medium">
                            Untuk agensi, konfigurasi seperti ini berguna saat membandingkan landing page, menguji kualitas konten, atau memisahkan traffic datacenter yang lemah dari sesi residensial yang memiliki parameter lebih jelas.
                        </p>
                    </div>
                    <div>
                        <h2 className="text-3xl font-black uppercase tracking-tight mb-5">Cara Tim Menggunakannya</h2>
                        <p className="text-gray-600 leading-relaxed font-medium mb-4">
                            Mulai dengan volume rendah, tentukan satu negara target, lalu baca perubahan metrik seperti durasi rata-rata, halaman per sesi, dan distribusi perangkat. Jika datanya stabil, perluas volume secara bertahap agar laporan tetap mudah dijelaskan.
                        </p>
                        <p className="text-gray-600 leading-relaxed font-medium">
                            Traffic Creator juga membantu membuat baseline untuk halaman baru. Anda dapat melihat apakah struktur internal link, judul, dan CTA cukup kuat sebelum mengarahkan kampanye berbayar atau kampanye konten yang lebih besar.
                        </p>
                        <p className="text-gray-600 leading-relaxed font-medium mt-4">
                            Untuk hasil yang mudah dibandingkan, simpan satu konfigurasi sebagai kontrol: negara target, perangkat, durasi sesi, dan jumlah halaman per kunjungan. Jalankan variasi kecil satu per satu agar perubahan laporan tidak tercampur.
                        </p>
                    </div>
                </div>
            </section>

            {/* Premium Comparison */}
            <section className="py-32 px-6 bg-white">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-black uppercase tracking-tight mb-4">Kualitas itu Penting</h2>
                        <p className="text-gray-500">Jangan pertaruhkan kualitas data Anda dengan lalu lintas bot murah.</p>
                    </div>

                    <div className="bg-white shadow-[0_10px_60px_rgba(0,0,0,0.05)] rounded-2xl overflow-hidden border border-gray-100">
                        <div className="grid grid-cols-3 bg-gray-900 text-white p-6 text-xs font-bold uppercase tracking-widest">
                            <div className="pl-4">Fitur</div>
                            <div className="text-[#ff5a0a] flex items-center gap-2"><Zap size={14} className="fill-[#ff5a0a]" /> Traffic Creator</div>
                            <div className="text-gray-500">Bot Murah</div>
                        </div>
                        <div className="divide-y divide-gray-50">
                            {comparisons.map((c, i) => (
                                <div key={i} className="grid grid-cols-3 p-6 items-center text-sm font-medium hover:bg-gray-50 transition-colors">
                                    <div className="font-bold text-gray-900 pl-4">{c.feature}</div>
                                    <div className="flex items-center gap-3 text-[#ff5a0a] font-bold">
                                        <CheckCircle size={18} className="ml-4" /> {c.us}
                                    </div>
                                    <div className="text-gray-400 flex items-center gap-3 pl-4">
                                        <X size={16} /> {c.them}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-24 px-6 bg-white border-t border-gray-100">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16 px-4">
                        <span className="text-[#ff5a0a] font-bold tracking-wider uppercase text-xs mb-4 block">Testimonials</span>
                        <h2 className="text-4xl font-black text-gray-900 mb-6">Kisah Sukses</h2>
                        <p className="text-xl text-gray-500 max-w-2xl mx-auto">
                            Temukan bagaimana para profesional membaca kualitas sesi dengan lalu lintas organik bergaya residensial.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=budi_s`} alt="Budi Santoso" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">Budi Santoso</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">Spesialis SEO</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">"Kontrol CTR dan durasi sesi dari aplikasi ini membantu kami membaca performa halaman klien dengan lebih jelas."</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=siti_r`} alt="Siti Rahma" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">Siti Rahma</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">Blogger & Publisher</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">"Akhirnya menemukan tool traffic dengan laporan GA4 yang bersih. Semua kunjungan menggunakan IP residensial dan parameter sesi yang jelas."</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                        <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 hover:shadow-lg transition-all">
                            <div className="flex items-center gap-4 mb-6">
                                <div className="w-12 h-12 rounded-full bg-gray-200 overflow-hidden">
                                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=rizky_p`} alt="Rizky Pratama" width="56" height="56" loading="lazy" />
                                </div>
                                <div>
                                    <div className="font-bold text-gray-900">Rizky Pratama</div>
                                    <div className="text-xs text-[#ff5a0a] font-bold uppercase tracking-wider">Pemilik Agensi</div>
                                </div>
                            </div>
                            <p className="text-gray-600 font-medium italic mb-6">"Fitur pencarian lokal dan deep-scrolling sangat membantu saat membandingkan kualitas halaman di beberapa pasar."</p>
                            <div className="flex gap-1 text-[#ff5a0a]">
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                                <Star size={16} fill="currentColor" />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-32 px-6 bg-[#ff5a0a] relative overflow-hidden">
                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tight mb-8 text-white">Mulai Analisis Traffic Sekarang</h2>
                    <p className="text-xl text-white/90 mb-12 font-medium max-w-2xl mx-auto">
                        Bandingkan kualitas halaman, baca laporan GA4, dan gunakan sesi residensial untuk memahami performa kampanye dengan lebih jelas.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-6 justify-center">
                        <Link to="/signup" className="bg-white text-black px-12 py-5 text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-all shadow-2xl flex items-center justify-center gap-3 rounded-sm transform hover:-translate-y-1">
                            Uji Coba Gratis <ArrowRight size={18} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-black text-white py-20 px-6 border-t border-gray-900 text-center">
                <div className="max-w-7xl mx-auto">
                    <div className="text-3xl font-black mb-8">TRAFFIC CREATOR</div>
                    <p className="text-gray-500 max-w-md mx-auto mb-12">
                        Standar industri untuk pembuatan lalu lintas SEO. Kami membantu agensi dan pemilik situs meningkatkan sinyal visibilitas pencarian mereka dengan aman.
                    </p>
                    <div className="text-xs text-gray-600">
                        © {new Date().getFullYear()} Traffic Creator. Seluruh hak cipta dilindungi undang-undang.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default IdOrganicWebsiteTraffic;
